/*
  Home Halloween section: one lightning strike when the scene first scrolls
  into view, and one each time a visitor taps the window or a cube.

  - A strike is the CSS one-shot in halloween-story.css (a double flicker, the
    jolt and the surprised faces). Nothing loops.
  - Strikes start at least two seconds apart; a tap in between does nothing.
  - Rain falls for five seconds after a strike, then holds still, so no motion
    runs by itself for more than five seconds (WCAG 2.2.2).
  - The Frankenstein cube above the heading starts under a purple and green
    shroud that swirls off once, the first time the card is on screen.
  - Reduced motion or forced colors: no strike, no shroud, and the tap button
    stays hidden. Story time and Sound belong to halloween-flavor.js.
*/
(() => {
  const STRIKE_GAP = 2000;
  const STRIKE_MS = 1700;
  const RAIN_MS = 5000;

  const lab = document.querySelector('[data-halloween-storm]');
  if (!lab) return;
  const button = lab.querySelector('[data-storm-strike]');

  const query = (text) => (window.matchMedia ? window.matchMedia(text) : null);
  const reduced = query('(prefers-reduced-motion: reduce)');
  const forced = query('(forced-colors: active)');
  const isCalm = () => Boolean((reduced && reduced.matches) || (forced && forced.matches));

  let lastStrike = -Infinity;
  let strikeTimer = 0;
  let rainTimer = 0;
  let alternate = false;
  let announced = false;

  const calm = () => {
    clearTimeout(strikeTimer);
    clearTimeout(rainTimer);
    lab.classList.remove('is-striking', 'is-raining');
  };

  // Returns whether the strike played.
  const strike = () => {
    const now = Date.now();
    if (isCalm() || now - lastStrike < STRIKE_GAP) return false;
    lastStrike = now;
    alternate = !alternate;
    lab.classList.toggle('is-alt', alternate);
    lab.classList.remove('is-striking');
    // Reading a layout property restarts the one-shot animations from their first frame.
    void lab.offsetWidth;
    lab.classList.add('is-striking', 'is-raining');
    clearTimeout(strikeTimer);
    clearTimeout(rainTimer);
    strikeTimer = setTimeout(() => lab.classList.remove('is-striking'), STRIKE_MS);
    rainTimer = setTimeout(() => lab.classList.remove('is-raining'), RAIN_MS);
    return true;
  };

  // The unveil: shroud the Frankenstein cube now, swirl it off once the card is on screen.
  const reveal = lab.closest('.halloween-story')?.querySelector('[data-halloween-unveil]');
  const unveil = (animate) => {
    if (!reveal || !reveal.classList.contains('is-shrouded')) return;
    reveal.classList.remove('is-shrouded');
    if (animate) reveal.classList.add('is-unveiled');
  };
  if (reveal && !isCalm() && 'IntersectionObserver' in window) {
    reveal.classList.add('is-shrouded');
    const veil = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      veil.disconnect();
      setTimeout(() => unveil(!isCalm()), 250);
    }, { threshold: 0.6 });
    veil.observe(reveal);
  }

  const sync = () => {
    if (button) button.hidden = isCalm();
    if (isCalm()) {
      calm();
      unveil(false);
    }
  };

  sync();
  [reduced, forced].forEach((list) => {
    if (list && list.addEventListener) list.addEventListener('change', sync);
  });

  if (button) button.addEventListener('click', strike);

  // The first strike waits until the scene is on screen; it never repeats on scroll.
  if ('IntersectionObserver' in window) {
    const watcher = new IntersectionObserver((entries) => {
      if (announced || !entries.some((entry) => entry.isIntersecting)) return;
      announced = true;
      watcher.disconnect();
      strike();
    }, { threshold: 0.4 });
    watcher.observe(lab);
  }
})();
